/**
 * Auth API Endpoints
 * Helpers for the passwordless SMS OTP sign-in flow
 */

import { apiClient } from "../client";
import type {
  ApiResult,
  ResolvePhoneUsernameRequest,
  ResolvePhoneUsernameResponse,
  RequestOptions,
} from "../types";

/**
 * Resolve the Cognito username (sub) for an existing user by phone number.
 *
 * Unauthenticated. Needed only for a specific Cognito edge case: an account
 * that has BOTH an email and a phone_number attribute, where the phone
 * number has never been verified. In that combination, Cognito's InitiateAuth
 * throws UserNotFoundException for USERNAME=<phone> even though the account
 * genuinely exists - e.g. a subscriber who purchased outside the product,
 * has an email on file, and never messaged in to verify their phone.
 * (Phone-only accounts with an unverified phone do NOT hit this - Cognito
 * resolves those fine. Unconfirmed accounts also don't need this - Cognito/
 * Amplify reports CONFIRM_SIGN_UP for those and the sign-up confirmation
 * flow handles it natively.) The returned username can be used in place of
 * the phone number to retry sign-in with the SMS_OTP challenge, which
 * verifies the phone number upon success.
 */
export async function resolvePhoneUsername(
  phoneNumber: string,
  options?: RequestOptions,
): Promise<ApiResult<ResolvePhoneUsernameResponse>> {
  return apiClient.post<ResolvePhoneUsernameResponse>(
    "/auth/resolve-phone",
    { phone_number: phoneNumber } satisfies ResolvePhoneUsernameRequest,
    options,
  );
}

export const authApi = {
  resolvePhoneUsername,
};

export default authApi;
